<template>
  <RateDetailTable
    :header="t('rateShop.details.quote')"
    :value="rateQuoteDetails"
  />
</template>

<script lang="ts" setup>
import { DateTime } from 'luxon';
import { computed, h, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { renderDateTime, titleCase } from '@rygen/rygen-ui/i18n';
import { CapacityProviderType } from '@rygen/rygen-ui/model';

import { RateQuote } from '@/model';

import RateDetailTable, {
  RateQuoteDetails,
} from '@/components/rateShop/expandedDetails/RateDetailTable.vue';

const props = defineProps({
  rateQuote: {
    type: RateQuote,
    default: () => new RateQuote(),
  },
  emailTender: {
    type: Boolean,
    default: () => false,
  },
});
const { rateQuote, emailTender } = toRefs(props);

const { n, t } = useI18n();

const rateQuoteDetails = computed(() => {
  return collectRateQuoteDetails(rateQuote.value);
});

const shouldShowMileageWarning = computed((): boolean => {
  return rateQuote.value.warnings.includes(
    t('rateShop.warnings.mileageRequestRejected'),
  );
});

const canReadContract = computed(() =>
  authorization.hasAuthority('contract', 'read'),
);

const collectRateQuoteDetails = (rateQuote: RateQuote): RateQuoteDetails[] => {
  const details: RateQuoteDetails[] = [];

  if (rateQuote.source === 'BROKERAGE' && rateQuote.brokeredCarrier) {
    details.push({
      k: t('brokerages.brokeredCarrier'),
      v: `${rateQuote.brokeredCarrier.name} ${
        rateQuote.brokeredCarrier.scac
          ? `(${rateQuote.brokeredCarrier.scac})`
          : ''
      }`,
    });
  }

  if (rateQuote.source === 'BROKERAGE' && rateQuote.brokeredCarrier?.mcNumber) {
    details.push({
      k: t('carriers.mcNumber'),
      v: rateQuote.brokeredCarrier.mcNumber,
    });
  }

  if (rateQuote.source === 'BROKERAGE' && rateQuote.serviceType) {
    details.push({
      k: t('rateShop.service'),
      v: rateQuote.serviceType,
    });
  }

  if (
    rateQuote.source === 'BROKERAGE' &&
    emailTender &&
    rateQuote.brokerageTenderConfigOverride?.configOptions?.notes
  ) {
    details.push({
      k: t('orders.notes'),
      v: rateQuote.brokerageTenderConfigOverride.configOptions.notes,
    });
  }

  if (rateQuote.currencyCode) {
    details.push({
      k: t('currency.currency'),
      v: rateQuote.currencyCode,
    });
  }

  if (rateQuote.totalMileage) {
    details.push({
      k: t('rateShop.quotedMileage'),
      v: n(rateQuote.totalMileage, 'basic'),
      tooltipIcon: 'pi pi-exclamation-triangle',
      tooltipText: shouldShowMileageWarning.value
        ? t('rateShop.warnings.mileageRequestRejected')
        : undefined,
    });
  }

  if (rateQuote.carrierQuoteNumber) {
    details.push({
      k: t('rateShop.details.carrierQuote'),
      v: rateQuote.carrierQuoteNumber,
    });
  }

  if (rateQuote.contractName) {
    details.push({
      k: t('rateShop.details.contract'),
      v:
        rateQuote.contractId && canReadContract.value
          ? () =>
              h('a', {
                href: `/lightning/contract/contracts/${rateQuote.contractId}`,
                textContent: rateQuote.contractName,
              })
          : rateQuote.contractName,
    });
  } else if (
    rateQuote.source === 'LANE_QUOTE' &&
    rateQuote.contractId &&
    canReadContract.value
  ) {
    details.push({
      k: t('rateShop.details.contract'),
      v: () =>
        h('a', {
          href: `/lightning/contract/laneQuotes/${rateQuote.contractId}`,
          textContent: t('rateShop.laneQuotes'),
        }),
    });
  }

  if (rateQuote.rateStrategyType) {
    details.push({
      k: t('rateShop.details.rateStrategy'),
      v: titleCase(rateQuote.rateStrategyType),
    });
  }

  if (rateQuote.laneType) {
    details.push({
      k: t('rateShop.details.laneType'),
      v: rateQuote.laneType,
    });
  }

  details.push({
    k: t('rateShop.rateSource'),
    v: rateQuote.isCapacityProvider
      ? t('rateShop.capacityProvider')
      : t(`rateShop.source.${rateQuote.source}`),
  });

  if (rateQuote.ratedDate) {
    details.push({
      k: t('rateShop.details.ratedDate'),
      v: renderDateTime(DateTime.fromJSDate(rateQuote.ratedDate)),
    });
  }

  if (rateQuote.quoteExpiration) {
    details.push({
      k: t('rateShop.details.quoteExpiration'),
      v: renderDateTime(DateTime.fromJSDate(rateQuote.quoteExpiration)),
    });
  }

  if (
    (rateQuote.brokeredCarrier?.type === CapacityProviderType.CARRIER ||
      rateQuote.source === 'BROKERAGE') &&
    rateQuote.carrier?.name
  ) {
    details.push({
      k: t('rateShop.capacityProvider'),
      v: rateQuote.carrier?.name,
    });
  }

  if (
    (rateQuote.brokeredCarrier?.type === CapacityProviderType.CARRIER ||
      rateQuote.source === 'BROKERAGE') &&
    rateQuote.brokeredCarrier?.name
  ) {
    details.push({
      k: t('brokerages.brokeredCarrier'),
      v: rateQuote.brokeredCarrier?.name,
    });
  }

  if (rateQuote.deliveryWindowStart || rateQuote.deliveryWindowEnd) {
    details.push({
      k: t('rateShop.details.deliveryWindow'),
      v: getDeliveryWindowString(
        rateQuote.deliveryWindowStart,
        rateQuote.deliveryWindowEnd,
      ),
    });
  }

  if (rateQuote.source !== 'CONTRACT' && rateQuote.createdBy) {
    details.push({
      k: t('rateShop.details.createdBy'),
      v: rateQuote.createdBy,
    });
  }

  if (rateQuote.billToOverrideId != null) {
    details.push({
      k: t('rateShop.details.billToOverride'),
      v: 'True',
    });
  }

  return details;
};

const getDeliveryWindowString = (
  start: string | undefined,
  end: string | undefined,
) => {
  if (start && end) {
    return `${start} - ${end}`;
  } else if (start) {
    return t('common.afterDateString', { dateString: start });
  } else {
    return t('common.beforeDateString', { dateString: end });
  }
};
</script>
